<template>
    <div v-if="isLoaded">
        <rkm-data-table
            :fields="fields"
            :repository="repositories.obito"
            ref="RkmDataTable"
            sort-by="nome"
        >
            <template v-slot:cell(nome)="{item}">
                {{ item.nome }}

                <span
                    v-if="item.bloqueado"
                    class="ml-1"
                    title="Cadastro bloqueado"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="ban" />
                </span>

                <span
                    v-if="isProvisorioOuTemporario(item)"
                    class="ml-1"
                    title="Paciente provisório/temporário"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="hourglass-half" />
                </span>

                <span
                    v-if="item.classificacaoId == enums.classificacao.OUTRO_MUNICIPIO"
                    class="ml-1"
                    title="Paciente não munícipe"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="map-pin" />
                </span>

                <span
                    v-if="!item.cpf && !item.cns"
                    class="ml-1"
                    title="Paciente sem CPF e CNS"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="exclamation-triangle" />
                </span>

                <span
                    v-if="item.pacienteResponsavelFamiliar"
                    class="ml-1"
                    title="Responsável familiar"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="crown" />
                </span>

                <span
                    v-if="isIdoso(item.dataNascimento)"
                    class="ml-1"
                    title="Idoso"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="blind" />
                </span>

                <span
                    v-if="hasTea(item)"
                    class="ml-1"
                    title="Pessoa com Transtorno do Espectro Autista - TEA"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="infinity" />
                </span>

                <span
                    v-if="hasPcd(item)"
                    class="ml-1"
                    title="Pessoa com Deficiência - PCD"
                    v-rkm-tooltip.hover
                >
                    <rkm-icon icon="wheelchair" />
                </span>
            </template>

            <template v-slot:cell(cns)="{item}">
                {{formatCns(item.cns)}}
            </template>

            <template v-slot:cell(actions)="{item}">
                <rkm-button
                    @click="edit(item.id)"
                    authorize="create-cadastro-obito"
                    color="primary"
                    small
                    title="Editar"
                >
                    <rkm-icon icon="pen" />
                </rkm-button>

                <rkm-button
                    @click="destroy(item)"
                    authorize="destroy-cadastro-obito"
                    color="danger"
                    small
                    title="Excluir"
                >
                    <rkm-icon icon="trash" />
                </rkm-button>
            </template>
        </rkm-data-table>

        <Modal
            ref="Modal"
            @handleSupervisorEditar="handleSupervisorEditar"
        />
    </div>
</template>

<script>
// Components.
import Modal from '@/components/cadastro/obito/Modal'

// Enums.
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

// Mixins.
import {error, formats, repository} from '@/mixins'

// Others.
import moment from 'moment'

export default {
    components: { Modal },
    created() {
        this.repositories = {
            obito: this.getConectaSusRepository('cadastro.obito'),
        }

        this.isLoaded = true
    },
    data: () => ({
        enums: { classificacao, subclassificacao, deficienciaBase, deficiencia },
        isLoaded: false,
        supervisorId: null,
        obitoId: null,
        fields: [
            {key: 'nome', label: 'Nome', name: 'cadastroCidadao.nome', searchable: true, sortable: true},
            {key: 'cns', label: 'CNS', name: 'cadastroCidadao.cns', searchable: true, sortable: true},
            {key: 'obitoEm', label: 'Data do óbito', name: 'cadastroObito.obitoEm', searchable: true, sortable: true},
            {key: 'declaracao', label: 'Declaração', name: 'cadastroObito.declaracao', searchable: true, sortable: true},
            {class: 'col-min', key: 'actions', label: 'Ações', name: 'actions'},
        ],
    }),
    methods: {
        isProvisorioOuTemporario(item) {

            return item.classificacaoId == this.enums.classificacao.PROVISORIO
                || item.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO
        },
        isIdoso(dataNascimento) {

            return !!dataNascimento && moment().diff(moment(dataNascimento), 'years') >= 60
        },
        getDeficienciasBaseIds(item) {

            return item.deficienciasBaseIds
                ? item.deficienciasBaseIds.split(',').map(Number)
                : []
        },
        hasTea(item) {

            return this.getDeficienciasBaseIds(item).includes(this.enums.deficienciaBase.TEA)
                || item.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        hasPcd(item) {

            return this.getDeficienciasBaseIds(item).some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!item.deficienciaId && item.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
        handleSupervisorEditar(supervisorId){

            this.supervisorId = supervisorId
            const id = this.obitoId

            this.$router.push({name: 'cadastro-obito-edit', params: {id}})
        },
        edit(id) {

            this.obitoId = id

            if (!this.supervisorId) {

                const cancel = async () => {

                    await this.$refs.Modal.cancel()
                }
                
                const confirm = async () => {

                    await this.$refs.Modal.open()
                }

                const msg = `Para continuar é necessário informar os dados do supervisor. Deseja continuar?`

                this.showConfirmation(msg, confirm, cancel)

                return
            }
        },
        destroy(item) {
            const destroy = () => this.repositories.obito.destroy(item.id)
                .then(() => this.$refs.RkmDataTable.refresh())

            this.showDeleteConfirmation('Deseja realmente excluir o óbito?', destroy)
        },
    },
    mixins: [error, formats, repository],
}
</script>